import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Coins, Globe, Info, CheckCircle, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Link } from "react-router-dom";

const STEPS = [
  { n: 1, t: "Visit any nearby jeweller", d: "Any local dealer of your choice — no partner list needed." },
  { n: 2, t: "Get a quote", d: "Ask the shop for their live rate for the quantity you want to convert." },
  { n: 3, t: "Submit to Metal Pocket", d: "Share dealer's name, UPI/bank, quoted price and grams below." },
  { n: 4, t: "We pay them directly", d: "Metal Pocket settles the dealer's UPI/bank on your behalf." },
  { n: 5, t: "Walk out with metal", d: "Dealer hands you physical gold/silver. Done." },
];

const METALS = ["gold", "silver", "platinum", "copper"];

export default function Dealers() {
  const { user } = useAuth();
  const { loc } = useUserLocation();
  const [wallet, setWallet] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [mode, setMode] = useState("self"); // self | assisted
  const [form, setForm] = useState({
    metal: "gold", quantity_g: 1,
    dealer_name: "", dealer_phone: "", dealer_address: "", dealer_city: "",
    dealer_upi_id: "", dealer_account_number: "", dealer_ifsc: "", dealer_gstin: "",
    quoted_price_per_g: "",
    delivery_address: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (user) {
      api.get("/metal-pocket/wallet").then(r => setWallet(r.data)).catch(() => {});
      api.get("/redemptions/mine").then(r => setRedemptions(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const availableMetals = wallet?.wallet?.map(w => w.metal) || [];
  const available = wallet?.wallet?.find(w => w.metal === form.metal);

  const submit = async () => {
    if (!user) { loginRedirect(); return; }
    if (Number(form.quantity_g) <= 0) { toast.error("Enter grams to redeem"); return; }
    setBusy(true);
    try {
      if (mode === "assisted") {
        const payload = {
          metal: form.metal,
          quantity_g: Number(form.quantity_g),
          delivery_address: form.delivery_address || null,
          delivery_lat: form.delivery_address ? loc.lat : null,
          delivery_lng: form.delivery_address ? loc.lng : null,
          customer_city: loc.city || "",
          customer_lat: loc.lat, customer_lng: loc.lng,
          notes: form.notes,
        };
        const r = await api.post("/metal-pocket/redeem-assisted", payload);
        toast.success(`${r.data.suggested_dealer ? "Vendor: " + r.data.suggested_dealer.name + " · " : ""}OTP ${r.data.pickup_otp} · Concierge will call you`);
      } else {
        if (!form.dealer_name) { toast.error("Enter dealer name"); setBusy(false); return; }
        if (!form.dealer_upi_id && !(form.dealer_account_number && form.dealer_ifsc)) {
          toast.error("Add dealer's UPI ID or bank account + IFSC"); setBusy(false); return;
        }
        const payload = {
          ...form,
          quantity_g: Number(form.quantity_g),
          quoted_price_per_g: form.quoted_price_per_g ? Number(form.quoted_price_per_g) : null,
        };
        const r = await api.post("/metal-pocket/redeem-anywhere", payload);
        toast.success(`Request submitted · OTP ${r.data.pickup_otp} · We settle ₹${r.data.settlement_amount_inr}`);
      }
      setForm(f => ({ ...f, dealer_name: "", dealer_phone: "", dealer_address: "", dealer_city: "", dealer_upi_id: "", dealer_account_number: "", dealer_ifsc: "", dealer_gstin: "", quoted_price_per_g: "", delivery_address: "", notes: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      {/* Hero */}
      <div className="grid lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#DDA77B] text-stone-900 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em]">
            <Globe size={12} /> Metal Pocket · Redeem Anywhere
          </div>
          <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-3">Walk into <span className="text-[#E05E36]">any nearby dealer.</span><br />We pay them <span className="italic underline decoration-[#DDA77B] decoration-[6px] underline-offset-4">directly.</span></h1>
          <p className="text-stone-600 mt-3 max-w-xl">No partner list required. Take your soft holding to <b>any</b> jeweller or bullion shop of your choice. Share the dealer's UPI or bank details with Metal Pocket — we settle the payment on your behalf. You walk out with real gold.</p>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-stone-200 rounded-3xl p-5 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-[#E05E36]" />
              <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">How it works</div>
            </div>
            <ol className="space-y-3">
              {STEPS.map(s => (
                <li key={s.n} className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#2D5A27] text-white font-extrabold text-xs grid place-items-center shrink-0 heading">{s.n}</div>
                  <div>
                    <div className="font-bold text-stone-900 text-sm">{s.t}</div>
                    <div className="text-xs text-stone-600">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* Redemption form */}
      <div className="mt-8 bg-white border border-stone-200 rounded-3xl p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Redeem physical</div>
            <h2 className="heading text-2xl font-extrabold text-stone-900 mt-1">Submit dealer settlement request</h2>
          </div>
          {wallet && <Link to="/metal-pocket" className="text-xs font-bold text-[#E05E36] flex items-center gap-1" data-testid="back-to-metal">Back to Metal Pocket <ArrowRight size={14} /></Link>}
        </div>

        {!user ? (
          <div className="mt-6 text-center py-10">
            <p className="text-stone-600 mb-4">Login to redeem your soft holding at any dealer.</p>
            <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="dealer-login">Login with Google</Button>
          </div>
        ) : availableMetals.length === 0 ? (
          <div className="mt-6 text-center py-10">
            <Coins size={32} className="mx-auto text-stone-300 mb-2" />
            <p className="text-stone-600 mb-4">You don't have any metal in your Metal Pocket wallet yet.</p>
            <Link to="/metal-pocket" className="inline-block"><Button className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]">Start ₹50 SIP →</Button></Link>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Mode toggle */}
            <div className="inline-flex bg-stone-100 rounded-full p-1 mb-2">
              {[
                { key: "self", label: "🚶 I'll walk in myself" },
                { key: "assisted", label: "✨ Walk me through it (concierge)" },
              ].map(m => (
                <button key={m.key} onClick={() => setMode(m.key)} className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${mode === m.key ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`} data-testid={`mode-${m.key}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Metal to redeem">
                <Select value={form.metal} onValueChange={v => set("metal", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="redeem-metal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {wallet.wallet.map(w => <SelectItem key={w.metal} value={w.metal}>{w.metal} · {w.quantity_g.toFixed(4)}g available</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Grams to redeem" required>
                <Input type="number" step="any" value={form.quantity_g} onChange={e => set("quantity_g", e.target.value)} className="rounded-xl" data-testid="redeem-grams" />
                {available && <div className="text-[10px] text-stone-500 mt-1">Available: {available.quantity_g.toFixed(4)} g</div>}
              </Field>
              {mode === "self" && (
                <Field label="Dealer's quoted rate (₹/g)">
                  <Input type="number" step="any" value={form.quoted_price_per_g} onChange={e => set("quoted_price_per_g", e.target.value)} placeholder="Optional — else live rate is used" className="rounded-xl" data-testid="redeem-quote" />
                </Field>
              )}
            </div>

            {mode === "self" ? (
              <div className="pt-4 border-t border-stone-100">
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Dealer details (any shop you choose)</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Shop / dealer name" required>
                    <Input value={form.dealer_name} onChange={e => set("dealer_name", e.target.value)} placeholder="e.g., Sharma Jewellers" className="rounded-xl" data-testid="dealer-name" />
                  </Field>
                  <Field label="Dealer phone">
                    <Input value={form.dealer_phone} onChange={e => set("dealer_phone", e.target.value)} className="rounded-xl" data-testid="dealer-phone" />
                  </Field>
                  <Field label="Shop address">
                    <Input value={form.dealer_address} onChange={e => set("dealer_address", e.target.value)} className="rounded-xl" data-testid="dealer-addr" />
                  </Field>
                  <Field label="City">
                    <Input value={form.dealer_city} onChange={e => set("dealer_city", e.target.value)} className="rounded-xl" data-testid="dealer-city" />
                  </Field>
                </div>
                <div className="mt-4">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2">Payment method (we pay the dealer here)</div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Field label="Dealer UPI ID">
                      <Input value={form.dealer_upi_id} onChange={e => set("dealer_upi_id", e.target.value)} placeholder="dealer@upi" className="rounded-xl" data-testid="dealer-upi" />
                    </Field>
                    <Field label="Bank account number">
                      <Input value={form.dealer_account_number} onChange={e => set("dealer_account_number", e.target.value)} className="rounded-xl" data-testid="dealer-account" />
                    </Field>
                    <Field label="IFSC">
                      <Input value={form.dealer_ifsc} onChange={e => set("dealer_ifsc", e.target.value.toUpperCase())} className="rounded-xl uppercase" data-testid="dealer-ifsc" />
                    </Field>
                  </div>
                  <div className="text-[11px] text-stone-500 mt-2">Provide UPI <b>OR</b> bank account + IFSC. Metal Pocket will settle payment to this account.</div>
                </div>
              </div>
            ) : (
              <div className="pt-4 border-t border-stone-100">
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Concierge (we handle everything)</div>
                <div className="p-3 bg-[#FFF3EC] rounded-xl text-xs text-stone-700 leading-relaxed">
                  ✨ Skip the dealer hunt. We'll pick a verified vendor near <b>{loc.city}</b> and coordinate the transaction. If you want it <b>home-delivered</b>, add an address below and we'll arrange transport (₹99 base + ₹8/km).
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <Field label="Home delivery address (optional)">
                    <Input value={form.delivery_address} onChange={e => set("delivery_address", e.target.value)} placeholder="Leave blank for pickup at vendor" className="rounded-xl" data-testid="delivery-addr" />
                  </Field>
                  <Field label="Notes / preferences">
                    <Input value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Preferred vendor city, purity, etc." className="rounded-xl" data-testid="concierge-notes" />
                  </Field>
                </div>
                <div className="mt-2 text-[11px] text-stone-500">Metal Pocket concierge will call you within 30 minutes to confirm vendor & schedule.</div>
              </div>
            )}

            <div className="pt-4 border-t border-stone-100 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-stone-500 flex items-start gap-2">
                <Info size={13} className="text-[#E05E36] mt-0.5 shrink-0" />
                {mode === "assisted"
                  ? "Concierge fee: dealer margin + optional transport. No hidden charges."
                  : "Deducts grams from your wallet immediately. Reversible if not yet settled."}
              </div>
              <Button onClick={submit} disabled={busy} className={`rounded-full gap-2 ${mode === "assisted" ? "bg-[#2D5A27] hover:bg-[#24491f]" : "bg-[#E05E36] hover:bg-[#c94e2a]"}`} data-testid="submit-redeem">
                <Coins size={16} /> {busy ? "Submitting…" : mode === "assisted" ? "Request concierge redemption" : "Submit redemption"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* My redemptions */}
      {user && redemptions.length > 0 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Your history</div>
          <h2 className="heading text-2xl font-extrabold text-stone-900 mt-1 mb-4">Redemption requests</h2>
          <div className="space-y-3">
            {redemptions.map(r => (
              <motion.div key={r.redemption_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`redemption-${r.redemption_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="heading font-bold text-stone-900">{r.dealer_name}</div>
                    <div className="text-xs text-stone-500">{r.quantity_g}g {r.metal} @ ₹{Math.round(r.price_per_g_at_request)}/g · Settle ₹{r.settlement_amount_inr}</div>
                    <div className="text-[10px] text-stone-500 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                      r.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                      r.status === "cancelled" ? "bg-stone-200 text-stone-700" : "bg-amber-100 text-amber-800"
                    }`}>{r.status}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                      r.settlement_status === "paid" ? "bg-[#2D5A27] text-white" : "bg-amber-100 text-amber-800"
                    }`}>settle: {r.settlement_status}</span>
                  </div>
                </div>
                <div className="mt-3 text-xs text-stone-600 flex flex-wrap gap-x-4 gap-y-1">
                  <span>OTP: <b className="text-stone-900">{r.pickup_otp}</b></span>
                  {r.dealer_upi_id && <span>UPI: {r.dealer_upi_id}</span>}
                  {r.dealer_account_number && <span>A/C: {r.dealer_account_number} · {r.dealer_ifsc}</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{label} {required && <span className="text-[#E05E36]">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
